"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const RESTClient_1 = tslib_1.__importDefault(require("../rest/RESTClient"));
const Level_1 = tslib_1.__importDefault(require("./Level"));
const stacktrace_js_1 = require("stacktrace-js");
const GlobalRemoteLogRegistry_1 = tslib_1.__importDefault(require("../util/GlobalRemoteLogRegistry"));
class RemoteLogService {
    constructor(moduleContext, version) {
        this.moduleContext = moduleContext;
        this.version = version;
        this.useStacktrace = false;
    }
    getModuleContext() {
        return this.moduleContext;
    }
    sendLog(entry) {
        try {
            this.addContextInfo(entry);
            entry.gwtModuleBaseURL = this.baseUrl;
            const restClient = new RESTClient_1.default({ version: this.version });
            restClient.post(this.baseUrl + "log", this.createFormData(entry), "Sender information om uventet fejl", { "Content-Type": "application/x-www-form-urlencoded; charset=utf-8" }).catch(e => {
                let msg = e.message;
                if (e.stack) {
                    const stack = e.stack;
                    msg += "\n" + stack;
                }
                if (console) {
                    console.log("Fejl ved logning af uventet fejl:\n" + msg);
                }
            });
        }
        catch (e1) {
            const name = e1.name;
            let msg = e1.message;
            if (e1.stack) {
                const stack = e1.stack;
                msg += "\n" + stack;
            }
            alert(`Fejl ved logning af uventet fejl:
             ${name}
             ${msg}`);
        }
    }
    log(level, message, error) {
        if (error) {
            if (this.useStacktrace) {
                (0, stacktrace_js_1.fromError)(error).then((st) => {
                    const log = {
                        ExceptionName: error.name,
                        Message: message || error.message,
                        SupportTag: error.supportTag ?? undefined,
                        TsException: JSON.stringify(this.convertStack(st, error)),
                        level: level
                    };
                    this.sendLog(log);
                }).catch((er) => {
                    const log = {
                        ExceptionName: error.name,
                        Message: "Problem deobfuscating stacktrace, message=" + er.message + ", original message=" + error.message,
                        SupportTag: error.supportTag ?? undefined,
                        TsRawException: error.stack,
                        level: level
                    };
                    this.sendLog(log);
                });
            }
            else {
                const errorEvent = this.mapErrorToErrorEvent(error);
                const log = {
                    ExceptionName: error.name,
                    Message: message || error.message,
                    SupportTag: error.supportTag ?? undefined,
                    TsRawException: JSON.stringify({
                        column: errorEvent.colno,
                        error: {
                            message: errorEvent.message,
                            stack: errorEvent.error.stack
                        },
                        file: errorEvent.filename,
                        line: errorEvent.lineno
                    }),
                    level: level
                };
                this.sendLog(log);
            }
        }
        else {
            const log = {
                Message: message,
                level: level
            };
            this.sendLog(log);
        }
    }
    doTry(fct, rethrow) {
        let result;
        try {
            result = fct();
        }
        catch (error) {
            this.log(Level_1.default.ERROR, "Ubehandlet exception", error);
            if (rethrow) {
                throw error;
            }
            else {
                return result;
            }
        }
    }
    logError(e) {
        // callback is called with an Array[StackFrame]
        if (!this.hasLocalCause(e)) {
            return;
        }
        if (e.error) {
            const customHandler = this.moduleContext.getUncaughtExceptionHandler();
            if (customHandler) {
                customHandler(e.error);
            }
            else {
                this.log(Level_1.default.ERROR, e.message, e.error);
                if (this.useStacktrace) {
                    (0, stacktrace_js_1.fromError)(e.error).then((st) => {
                        const log = {
                            ExceptionName: e.error.name,
                            Message: e.message,
                            SupportTag: e.error.supportTag ?? undefined,
                            TsException: JSON.stringify(this.convertStack(st, e.error)),
                            level: "ERROR"
                        };
                        this.sendLog(log);
                    }).catch((er) => {
                        console.error(er);
                    });
                }
                else {
                    const log = {
                        ExceptionName: e.error.name,
                        Message: e.message,
                        SupportTag: e.error.supportTag ?? undefined,
                        TsRawException: JSON.stringify({
                            column: e.colno,
                            error: {
                                message: e.message,
                                stack: e.error.stack
                            },
                            file: e.filename,
                            line: e.lineno
                        }),
                        level: "ERROR"
                    };
                    this.sendLog(log);
                }
            }
        }
        else {
            // Edge, IE <= 10, Safari does not provide the 5th error argument - simulate one with a single line stacktrace
            const log = {
                Message: e.message,
                TsRawException: JSON.stringify({
                    column: e.colno,
                    error: {
                        message: e.message,
                        stack: `at ${e.filename}:${e.lineno}:${e.colno}`
                    },
                    file: e.filename,
                    line: e.lineno
                }),
                level: "ERROR"
            };
            this.sendLog(log);
        }
    }
    setupErrorHandler(useStacktrace = false) {
        this.useStacktrace = useStacktrace;
        const remoteLogRegistry = GlobalRemoteLogRegistry_1.default.getRegistry();
        remoteLogRegistry.register(this);
    }
    get baseUrl() {
        if (!this._baseUrl) {
            const url = this.guessBaseUrl();
            if (url) {
                this._baseUrl = url;
            }
        }
        return this._baseUrl;
    }
    set baseUrl(baseUrl) {
        this._baseUrl = baseUrl;
    }
    guessBaseUrl() {
        const localSourceUrl = this.getLocalSourceUrl();
        if (localSourceUrl) {
            const url = new URL(localSourceUrl);
            // This is just a guess: The first path element of the local source url
            const baseUrl = url.pathname.replace(/(\/[^\/]*\/).*/, "$1");
            return baseUrl;
        }
    }
    mapErrorToErrorEvent(error) {
        const { filename, lineno, colno } = this.extractFileLineColFromStack(error.stack);
        return {
            colno: colno,
            error: error,
            filename: filename,
            lineno: lineno,
            message: error.message
        };
    }
    extractFileLineColFromStack(stack) {
        const firstLine = stack?.split("\n").find(l => this.getSourceUrlFromStackLine(l));
        return firstLine && this.getFileLineColFromStackLine(firstLine) || {
            colno: undefined,
            filename: undefined,
            lineno: undefined
        };
    }
    hasLocalCause(errorEvent) {
        const error = new Error();
        let localStackTrace = error.stack;
        const sourceURL = error.sourceURL;
        if (errorEvent.error.sourceURL && sourceURL) {
            // Works in safari
            return errorEvent.error.sourceURL === sourceURL;
        }
        if (!localStackTrace) {
            // IE 10+11 will only create a stack trace if error is thrown and catched
            try {
                throw new Error();
            }
            catch (e) {
                localStackTrace = e.stack;
            }
        }
        const errorStackTrace = errorEvent?.error?.stack;
        if (localStackTrace && errorStackTrace) {
            // First, find the first line of a local stack trace that mentions the local method
            const line = localStackTrace.split("\n").find((s) => s.indexOf("hasLocalCause") >= 0);
            if (line) {
                // Second, try to extract a javascript source url from the match
                const localSourceUrl = this.getSourceUrlFromStackLine(line);
                const errorLines = errorStackTrace.split("\n").filter((s) => s.indexOf(".js") >= 0);
                return errorLines.some(s => localSourceUrl === this.getSourceUrlFromStackLine(s));
            }
        }
        return false;
    }
    getLocalSourceUrl() {
        const error = new Error();
        let localStackTrace = error.stack;
        if (!localStackTrace) {
            // IE 10+11 will only create a stack trace if error is thrown and catched
            try {
                throw new Error();
            }
            catch (e) {
                localStackTrace = e.stack;
            }
        }
        if (localStackTrace) {
            // First, find the first line of a local stack trace that mentions the local method
            const line = localStackTrace.split("\n").find((s) => s.indexOf("getLocalSourceUrl") >= 0);
            if (line) {
                // Second, try to extract a javascript source url from the match
                return this.getSourceUrlFromStackLine(line);
            }
        }
    }
    getSourceUrlFromStackLine(line) {
        const match = line.match(/(http|https):\/\/(\w*)(:\d+)?(.*\.js)/);
        return match && match[0];
    }
    getFileLineColFromStackLine(line) {
        const match = line.match(/((http|https):\/\/(\w*)(:\d+)?(.*\.js)(?:\?[^:]*)?):(\d+):(\d+)/);
        return match && {
            colno: match[7],
            filename: match[1],
            lineno: match[6]
        };
    }
    convertStack(stack, er) {
        let res = {
            exceptionName: er.name,
            message: er.message,
            stack: []
        };
        for (let frame of stack) {
            res.stack.push({
                column: frame.columnNumber,
                fct: frame.functionName,
                file: frame.fileName,
                line: frame.lineNumber
            });
        }
        return res;
    }
    addContextInfo(entry) {
        entry.AppCtx = this.moduleContext.getApplicationContextId();
        entry.TsModuleName = this.moduleContext.getModuleName();
        const userContext = this.moduleContext.getUserContext();
        if (userContext) {
            entry.UserCommonName = userContext.getCommonName();
            entry.UserCpr = userContext.getCpr();
            entry.UserCvr = userContext.getCvr();
            entry.OnBehalfOfAuthNo = userContext.getOnBehalfOfAutorisationsnummer();
            entry.EffectiveAuthNo = userContext.getEffectiveAuthId();
            const userOrg = userContext.getOrganisation();
            if (userOrg) {
                entry.OrgName = userContext.getOrganisation().getOrganisationName();
                entry.HospitalOrgId = userOrg.getHospitalOrganisationIdentifier();
                const doctorOrgId = userOrg.getDoctorOrganisationIdentifier();
                if (doctorOrgId) {
                    entry.DoctorOrgId = doctorOrgId.toString();
                }
                const municipalityOrdId = userOrg.getMunicipalityOrganisationIdentifier();
                if (municipalityOrdId) {
                    entry.MunicipalityOrgId = municipalityOrdId.toString();
                }
                entry.AdminId = userOrg.getLMSAdministratorIdentifier();
                entry.Tlf = userOrg.getTelephoneNumberIdentifier();
                const addr = userOrg.getAddressLines();
                if (addr) {
                    for (let i = 0; i < addr.length; i++) {
                        entry["address" + i + 1] = addr[i];
                    }
                }
            }
        }
        if (this.moduleContext.getPatient()) {
            entry.Patient = this.moduleContext.getPatient().getCpr();
        }
    }
    createFormData(entry) {
        let formData = "";
        for (const [key, value] of Object.entries(entry)) {
            if (value) {
                if (formData.length > 0) {
                    formData += "&";
                }
                formData += encodeURIComponent(key) + "=" + encodeURIComponent(value);
            }
        }
        return formData;
    }
}
RemoteLogService.deps = () => ["ModuleContext", "Version"];
exports.default = RemoteLogService;
//# sourceMappingURL=RemoteLogService.js.map